import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../constants/appConstants';

export interface AfterHoursOverride {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  reason: string;
  managerId: string;
  createdAt: string;
}

interface AddOverrideData {
  employeeId: string;
  employeeName: string;
  date: string;
  reason?: string;
}

export const useAfterHoursOverrides = (managerId?: string, isManager: boolean = false) => {
  const [overrides, setOverrides] = useState<AfterHoursOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasOverrideToday, setHasOverrideToday] = useState(false);

  const fetchOverrides = useCallback(async () => {
    if (!isManager || !managerId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/task-estimates/settings/after-hours-overrides`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOverrides(data.overrides || []);
      }
    } catch (err) {
      console.error('Failed to fetch after-hours overrides:', err);
    } finally {
      setLoading(false);
    }
  }, [isManager, managerId]);

  const checkMyOverride = useCallback(async (employeeId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/task-estimates/settings/after-hours-override/${employeeId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setHasOverrideToday(data.hasOverride);
        return data.hasOverride;
      }
    } catch (err) {
      console.error('Failed to check after-hours override:', err);
    }
    return false;
  }, []);

  const addOverride = useCallback(async (data: AddOverrideData): Promise<{ success: boolean; message?: string }> => {
    if (!managerId) return { success: false, message: 'Not authenticated' };

    try {
      const response = await fetch(`${API_URL}/task-estimates/settings/after-hours-overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...data, managerId })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        await fetchOverrides();
        return { success: true };
      }
      return { success: false, message: result.error || 'Failed to add override' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Failed to add override' };
    }
  }, [managerId, fetchOverrides]);

  const removeOverride = useCallback(async (overrideId: string): Promise<{ success: boolean; message?: string }> => {
    if (!managerId) return { success: false, message: 'Not authenticated' };

    try {
      const response = await fetch(
        `${API_URL}/task-estimates/settings/after-hours-overrides/${overrideId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ managerId })
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        await fetchOverrides();
        return { success: true };
      }
      return { success: false, message: result.error || 'Failed to remove override' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Failed to remove override' };
    }
  }, [managerId, fetchOverrides]);

  useEffect(function loadOverridesOnMount() {
    if (isManager) {
      fetchOverrides();
    }
  }, [isManager, fetchOverrides]);

  return {
    overrides,
    loading,
    hasOverrideToday,
    fetchOverrides,
    checkMyOverride,
    addOverride,
    removeOverride
  };
};
